// Retain raster content on the GPU; layout updates only rebuild positioned draw commands.
export const createDomRenderer = (root, { scene, invalidate = () => {}, transparentBackground = false, aboveZIndex = null, maxZIndex = null } = {}) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const layer = scene.createLayer();
  const resources = new Map();
  let commands = [];
  let visibleVideos = new Set();
  let canvasCommands = [];
  let activeClip;
  let background;
  let dirty = true;
  let assetVersion = 0;
  let fontVersion = 0;
  const textCache = new WeakMap();
  const svgCache = new WeakMap();
  const backgrounds = new Map();
  const videoFrames = new Map();
  let styles = new WeakMap();
  let rectangles = new WeakMap();
  let textRectangles = new WeakMap();
  const getStyle = element => {
    if (!styles.has(element)) styles.set(element, getComputedStyle(element));
    return styles.get(element);
  };
  const getRect = element => {
    if (!rectangles.has(element)) rectangles.set(element, element.getBoundingClientRect());
    return rectangles.get(element);
  };
  let width, height, dpr;
  const range = document.createRange();
  const transparent = color => !color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)';
  const size = value => parseFloat(value) || 0;
  const fontStretches = {
    '50%': 'ultra-condensed', '62.5%': 'extra-condensed', '75%': 'condensed',
    '87.5%': 'semi-condensed', '100%': 'normal', '112.5%': 'semi-expanded',
    '125%': 'expanded', '150%': 'extra-expanded', '200%': 'ultra-expanded',
  };
  const textRendering = {
    auto: 'auto', optimizespeed: 'optimizeSpeed',
    optimizelegibility: 'optimizeLegibility', geometricprecision: 'geometricPrecision',
  };

  const path = (rect, radius = 0) => {
    ctx.beginPath();
    ctx.roundRect(rect.left, rect.top, rect.width, rect.height, Math.min(radius, rect.width / 2, rect.height / 2));
  };

  const clip = (rect, style, parent) => {
    const result = { ...parent };
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
      if (style.overflowX !== 'visible') {
        result.left = Math.max(result.left, rect.left);
        result.right = Math.min(result.right, rect.right);
      }
      if (style.overflowY !== 'visible') {
        result.top = Math.max(result.top, rect.top);
        result.bottom = Math.min(result.bottom, rect.bottom);
      }
    }
    const inset = style.clipPath.match(/^inset\(([^)]*)\)/);
    if (inset) {
      const values = inset[1].split('round')[0].trim().split(/\s+/);
      const offsets = [values[0], values[1] || values[0], values[2] || values[0], values[3] || values[1] || values[0]];
      const pixels = offsets.map((value, index) => size(value) * (value.endsWith('%') ? (index % 2 ? rect.width : rect.height) / 100 : 1));
      result.left = Math.max(result.left, rect.left + pixels[3]);
      result.top = Math.max(result.top, rect.top + pixels[0]);
      result.right = Math.min(result.right, rect.right - pixels[1]);
      result.bottom = Math.min(result.bottom, rect.bottom - pixels[2]);
    }
    return result;
  };
  const resourceFor = (element, slot) => {
    let slots = resources.get(element);
    if (!slots) resources.set(element, slots = new Map());
    let resource = slots.get(slot);
    if (!resource) {
      resource = scene.createTexture();
      slots.set(slot, resource);
    }
    return resource;
  };
  const raster = (element, slot, signature, rect, paint) => {
    const resource = resourceFor(element, slot);
    const maxSize = scene.maxTextureSize || Infinity;
    const ratio = Math.min(dpr, maxSize / rect.width, maxSize / rect.height);
    const key = `${Math.round(rect.width * ratio)}:${Math.round(rect.height * ratio)}:${ratio}:${signature}`;
    if (resource.key !== key) {
      canvas.width = Math.max(1, Math.ceil(rect.width * ratio));
      canvas.height = Math.max(1, Math.ceil(rect.height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, -rect.left * ratio, -rect.top * ratio);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      paint();
      scene.upload(resource, canvas);
      resource.key = key;
    }
    return resource;
  };
  const add = (rect, opacity, properties) => {
    commands.push({ rect, opacity, clip: activeClip, ...properties });
  };
  const loaded = () => {
    assetVersion++;
    invalidate();
  };

  const drawText = (node, style, element) => {
    let tokens = textCache.get(node);
    if (!tokens || tokens.text !== node.textContent) {
      tokens = { text: node.textContent, words: [...node.textContent.matchAll(/\S+/g)] };
      textCache.set(node, tokens);
    }
    ctx.fillStyle = style.color;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.direction = style.direction;
    let scale = 1;
    for (let parent = element; parent && parent !== root; parent = parent.parentElement) {
      const transform = getStyle(parent).transform;
      if (transform !== 'none') {
        const matrix = new DOMMatrixReadOnly(transform);
        scale *= Math.hypot(matrix.m11, matrix.m12, matrix.m13);
      }
    }
    ctx.font = `${style.fontStyle} ${style.fontWeight} ${size(style.fontSize) * scale}px ${style.fontFamily}`;
    if ('fontKerning' in ctx) ctx.fontKerning = style.fontKerning;
    if ('fontStretch' in ctx) {
      ctx.fontStretch = fontStretches[style.fontStretch]
        || (Object.values(fontStretches).includes(style.fontStretch) ? style.fontStretch : 'normal');
    }
    if ('textRendering' in ctx) ctx.textRendering = textRendering[style.textRendering.toLowerCase()] || 'auto';
    if ('letterSpacing' in ctx) ctx.letterSpacing = `${size(style.letterSpacing) * scale}px`;
    for (const word of tokens.words) {
      let words = textRectangles.get(node);
      if (!words) textRectangles.set(node, words = new Map());
      let rect = words.get(word.index);
      if (!rect) {
        range.setStart(node, word.index);
        range.setEnd(node, word.index + word[0].length);
        rect = range.getBoundingClientRect();
        words.set(word.index, rect);
      }
      if (!rect.width || rect.bottom < 0 || rect.top > height || rect.right < 0 || rect.left > width) continue;
      let text = word[0];
      if (style.textTransform === 'uppercase') text = text.toUpperCase();
      if (style.textTransform === 'lowercase') text = text.toLowerCase();
      if (style.textTransform === 'capitalize') text = text[0].toUpperCase() + text.slice(1);
      const metrics = ctx.measureText(text);
      ctx.fillText(text, rect.left, rect.top + (metrics.fontBoundingBoxAscent || size(style.fontSize) * scale * 0.8));
      if (style.textDecorationLine.includes('underline')) ctx.fillRect(rect.left, rect.bottom - 1, rect.width, Math.max(1, size(style.textDecorationThickness)));
    }
  };

  const drawMedia = (source, rect, style) => {
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    if (!sw || !sh || !rect.width || !rect.height) return;
    ctx.save();
    path(rect, size(style.borderTopLeftRadius));
    ctx.clip();
    if (style.objectFit === 'cover' || style.objectFit === 'contain') {
      const ratio = style.objectFit === 'cover' ? Math.max(rect.width / sw, rect.height / sh) : Math.min(rect.width / sw, rect.height / sh);
      const positions = style.objectPosition.split(' ');
      const position = value => value?.endsWith('%') ? size(value) / 100 : value === 'left' || value === 'top' ? 0 : value === 'right' || value === 'bottom' ? 1 : 0.5;
      ctx.drawImage(source, rect.left + (rect.width - sw * ratio) * position(positions[0]), rect.top + (rect.height - sh * ratio) * position(positions[1]), sw * ratio, sh * ratio);
    } else ctx.drawImage(source, rect.left, rect.top, rect.width, rect.height);
    ctx.restore();
  };

  const releaseVideo = (video, frame) => {
    if (frame.callback !== undefined) video.cancelVideoFrameCallback(frame.callback);
    videoFrames.delete(video);
  };

  const videoResource = video => {
    let frame = videoFrames.get(video);
    if (frame && frame.source !== video.currentSrc) {
      releaseVideo(video, frame);
      frame.resource.version = 0;
      frame = null;
    }
    if (!frame) {
      frame = { resource: resourceFor(video, 'media'), source: video.currentSrc, dirty: true, time: -1 };
      videoFrames.set(video, frame);
      if (video.requestVideoFrameCallback) {
        const onFrame = () => {
          frame.dirty = true;
          frame.callback = video.requestVideoFrameCallback(onFrame);
        };
        frame.callback = video.requestVideoFrameCallback(onFrame);
      }
    }
    return frame.resource;
  };

  const addMedia = (element, source, rect, style, opacity, resource) => {
    const sw = source.videoWidth || source.naturalWidth || source.width || resource?.width;
    const sh = source.videoHeight || source.naturalHeight || source.height || resource?.height;
    if (!sw || !sh) return;
    if (!resource) {
      resource = resourceFor(element, 'media');
      const key = `${element.currentSrc || element.src || ''}:${sw}:${sh}`;
      if (resource.key !== key || element instanceof HTMLCanvasElement) {
        scene.upload(resource, source);
        resource.key = key;
      }
    }
    const positions = style.objectPosition.split(' ');
    const position = value => value?.endsWith('%') ? size(value) / 100 : value === 'left' || value === 'top' ? 0 : value === 'right' || value === 'bottom' ? 1 : 0.5;
    let destination = rect;
    let uv = [0, 0, 1, 1];
    if (style.objectFit === 'cover' || style.objectFit === 'contain') {
      const ratio = style.objectFit === 'cover' ? Math.max(rect.width / sw, rect.height / sh) : Math.min(rect.width / sw, rect.height / sh);
      if (style.objectFit === 'cover') {
        const u = rect.width / (sw * ratio);
        const v = rect.height / (sh * ratio);
        uv = [(1 - u) * position(positions[0]), (1 - v) * position(positions[1]), u, v];
      } else {
        const left = rect.left + (rect.width - sw * ratio) * position(positions[0]);
        const top = rect.top + (rect.height - sh * ratio) * position(positions[1]);
        destination = { left, top, width: sw * ratio, height: sh * ratio };
      }
    }
    const radius = style.borderTopLeftRadius.endsWith('%') ? size(style.borderTopLeftRadius) / 100 * Math.min(rect.width, rect.height) : size(style.borderTopLeftRadius);
    add(destination, opacity, { resource, shape: rect, radius, uv, video: element instanceof HTMLVideoElement ? element : null,
      canvasSource: element instanceof HTMLCanvasElement ? element : null });
  };

  const drawSvg = (element, rect, style) => {
    const markup = element.outerHTML;
    let cached = svgCache.get(element);
    if (!cached || cached.markup !== markup || cached.color !== style.color) {
      const clone = element.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', Math.max(1, rect.width));
      clone.setAttribute('height', Math.max(1, rect.height));
      clone.style.color = style.color;
      const image = new Image();
      image.onload = loaded;
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
      cached = { markup, color: style.color, image };
      svgCache.set(element, cached);
    }
    if (cached.image.complete && cached.image.naturalWidth) drawMedia(cached.image, rect, style);
  };

  const drawBackground = (rect, style) => {
    const value = style.backgroundImage;
    if (value === 'none') return;
    const url = value.match(/^url\(["']?(.*?)["']?\)$/);
    if (url) {
      let image = backgrounds.get(url[1]);
      if (!image) {
        image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = loaded;
        image.src = url[1];
        backgrounds.set(url[1], image);
      }
      if (image.complete && image.naturalWidth) drawMedia(image, rect, {
        ...style,
        objectFit: style.backgroundSize === 'contain' ? 'contain' : 'cover',
        objectPosition: style.backgroundPosition,
      });
      return;
    }
    if (!value.startsWith('linear-gradient(')) return;
    const stops = [...value.matchAll(/(rgba?\([^)]*\)|#[\da-f]+|transparent)(?:\s+(-?[\d.]+)%?)?/gi)];
    if (stops.length < 2) return;
    const angle = value.match(/^linear-gradient\((-?[\d.]+)deg/);
    const radians = (angle ? Number(angle[1]) : 180) * Math.PI / 180;
    const dx = Math.sin(radians);
    const dy = -Math.cos(radians);
    const length = Math.abs(rect.width * dx) + Math.abs(rect.height * dy);
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const gradient = ctx.createLinearGradient(cx - dx * length / 2, cy - dy * length / 2, cx + dx * length / 2, cy + dy * length / 2);
    stops.forEach((stop, index) => gradient.addColorStop(Math.max(0, Math.min(1, stop[2] === undefined ? index / (stops.length - 1) : Number(stop[2]) / 100)), stop[1]));
    ctx.fillStyle = gradient;
    path(rect, size(style.borderTopLeftRadius));
    ctx.fill();
  };

  const drawBorders = (element, rect, style) => {
    const width = element.offsetWidth || rect.width;
    const height = element.offsetHeight || rect.height;
    const radius = style.borderTopLeftRadius;
    const circular = radius.endsWith('%') ? size(radius) >= 50 : size(radius) >= Math.min(width, height) / 2;
    if (circular) {
      ctx.save();
      ctx.translate(rect.left + rect.width / 2, rect.top + rect.height / 2);
      if (style.transform !== 'none') {
        const matrix = new DOMMatrixReadOnly(style.transform);
        ctx.rotate(Math.atan2(matrix.m12, matrix.m11));
        ctx.scale(Math.hypot(matrix.m11, matrix.m12), Math.hypot(matrix.m21, matrix.m22));
      }
      ['Top', 'Right', 'Bottom', 'Left'].forEach((side, index) => {
        const thickness = size(style[`border${side}Width`]);
        if (!thickness || style[`border${side}Style`] === 'none') return;
        ctx.beginPath();
        ctx.lineWidth = thickness;
        ctx.strokeStyle = style[`border${side}Color`];
        const start = -Math.PI * 0.75 + index * Math.PI / 2;
        ctx.ellipse(0, 0, Math.max(0.01, (width - thickness) / 2), Math.max(0.01, (height - thickness) / 2), 0, start, start + Math.PI / 2);
        ctx.stroke();
      });
      ctx.restore();
      return;
    }
    const borders = [
      ['Top', rect.left, rect.top, rect.width, size(style.borderTopWidth)],
      ['Bottom', rect.left, rect.bottom - size(style.borderBottomWidth), rect.width, size(style.borderBottomWidth)],
      ['Left', rect.left, rect.top, size(style.borderLeftWidth), rect.height],
      ['Right', rect.right - size(style.borderRightWidth), rect.top, size(style.borderRightWidth), rect.height],
    ];
    for (const [side, x, y, w, h] of borders) {
      if (style[`border${side}Style`] === 'none' || !w || !h) continue;
      ctx.fillStyle = style[`border${side}Color`];
      ctx.fillRect(x, y, w, h);
    }
  };

  const isStackingContext = (element, style) => {
    const positioned = style.position !== 'static';
    const parentDisplay = element.parentElement ? getStyle(element.parentElement).display : '';
    return style.position === 'fixed' || style.position === 'sticky'
      || (style.zIndex !== 'auto' && (positioned || /flex|grid/.test(parentDisplay)))
      || Number(style.opacity) < 1 || style.transform !== 'none'
      || style.perspective !== 'none' || style.filter !== 'none'
      || style.clipPath !== 'none'
      || style.mixBlendMode !== 'normal' || style.isolation === 'isolate'
      || /transform|opacity/.test(style.willChange);
  };

  const paintChildren = (element, opacity) => {
    const entries = [];
    const collect = (child, ancestors = []) => {
      if (child.id === 'cursor' || child.id === 'header' && child !== root) return;
      const style = getStyle(child);
      if (maxZIndex !== null && size(style.zIndex) > maxZIndex) return;
      if (style.display === 'none' || style.contentVisibility === 'hidden') return;
      if (Number(style.opacity) < 0.001) return;
      const stackingContext = isStackingContext(child, style);
      const above = aboveZIndex === null || element !== root || size(style.zIndex) > aboveZIndex;
      const context = stackingContext && above;
      const z = context ? size(style.zIndex) : 0;
      const rank = z < 0 ? -1 : z > 0 ? 2 : context || style.position !== 'static' ? 1 : 0;
      if (above) entries.push({ child, ancestors, context, z, rank, order: entries.length });
      if (!context && child.tagName.toLowerCase() !== 'svg') {
        for (const nested of child.children) collect(nested, [...ancestors, child]);
      }
    };
    for (const child of element.children) collect(child);
    entries.sort((a, b) => a.rank - b.rank || a.z - b.z || a.order - b.order);
    for (const entry of entries) {
      const previousClip = activeClip;
      for (const ancestor of entry.ancestors) activeClip = clip(getRect(ancestor), getStyle(ancestor), activeClip);
      paint(entry.child, opacity, !entry.context);
      activeClip = previousClip;
    }
  };

  const paint = (element, inheritedOpacity = 1, skipChildren = false) => {
    if (element.id === 'cursor' || element.id === 'header' && element !== root) return;
    if (element.classList.contains('webgl-dom-wrap') || /^(SCRIPT|STYLE|LINK|NOSCRIPT|IFRAME|SOURCE)$/.test(element.tagName)) return;
    const style = getStyle(element);
    if (style.display === 'none' || style.contentVisibility === 'hidden') return;
    // Only the root is hidden by CSS; its descendants retain their real styles.
    const opacity = inheritedOpacity * (element === root ? 1 : Number(style.opacity));
    if (opacity < 0.001) return;
    const rect = getRect(element);
    if ((style.overflowX !== 'visible' && style.overflowY !== 'visible' || style.clipPath !== 'none')
      && (rect.bottom < 0 || rect.top > height || rect.right < 0 || rect.left > width)) return;
    if (element === root && aboveZIndex !== null) {
      paintChildren(element, opacity);
      return;
    }
    const previousClip = activeClip;
    activeClip = clip(rect, style, activeClip);
    if (activeClip.right <= activeClip.left || activeClip.bottom <= activeClip.top) {
      activeClip = previousClip;
      return;
    }
    if (style.visibility === 'visible' && rect.width && rect.height && rect.bottom >= 0 && rect.top <= height) {
      const radius = style.borderTopLeftRadius.endsWith('%') ? size(style.borderTopLeftRadius) / 100 * Math.min(rect.width, rect.height) : size(style.borderTopLeftRadius);
      if (!transparent(style.backgroundColor)) {
        add(rect, opacity, { fill: style.backgroundColor, radius });
      }
      if (style.backgroundImage !== 'none') {
        const signature = [style.backgroundImage, style.backgroundSize, style.backgroundPosition, radius, assetVersion].join('|');
        add(rect, opacity, { resource: raster(element, 'background', signature, rect, () => drawBackground(rect, style)), radius });
      }
      const sides = ['Top', 'Right', 'Bottom', 'Left'];
      if (sides.some(side => size(style[`border${side}Width`]) && style[`border${side}Style`] !== 'none')) {
        const signature = sides.flatMap(side => [style[`border${side}Width`], style[`border${side}Color`], style[`border${side}Style`]]).concat(radius, style.transform).join('|');
        add(rect, opacity, { resource: raster(element, 'borders', signature, rect, () => drawBorders(element, rect, style)) });
      }
      if (element instanceof HTMLImageElement && element.complete && element.naturalWidth) addMedia(element, element, rect, style, opacity);
      else if (element instanceof HTMLVideoElement) addMedia(element, element, rect, style, opacity, videoResource(element));
      else if (element instanceof HTMLCanvasElement) addMedia(element, element, rect, style, opacity);
      else if (element.tagName.toLowerCase() === 'svg') {
        const signature = [element.outerHTML, style.color, assetVersion].join('|');
        add(rect, opacity, { resource: raster(element, 'svg', signature, rect, () => drawSvg(element, rect, style)) });
      } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        const signature = [element.value, element.placeholder, style.fontWeight, style.fontSize, style.fontFamily, style.color, style.paddingLeft, fontVersion].join('|');
        add(rect, opacity, { resource: raster(element, 'input', signature, rect, () => {
          ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
          ctx.fillStyle = style.color;
          ctx.textBaseline = 'middle';
          ctx.fillText(element.value || element.placeholder || '', rect.left + size(style.paddingLeft), rect.top + rect.height / 2);
        }) });
      }
    }
    if (element.tagName.toLowerCase() !== 'svg') {
      const textNodes = [...element.childNodes].filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNodes.length && style.visibility === 'visible' && rect.width && rect.height && rect.bottom >= 0 && rect.top <= height) {
        let scale = 1;
        for (let parent = element; parent && parent !== root; parent = parent.parentElement) {
          const transform = getStyle(parent).transform;
          if (transform !== 'none') {
            const matrix = new DOMMatrixReadOnly(transform);
            scale *= Math.hypot(matrix.m11, matrix.m12, matrix.m13);
          }
        }
        const signature = [textNodes.map(node => node.textContent).join(''), style.fontStyle, style.fontWeight, style.fontSize, style.fontFamily,
          style.fontKerning, style.fontStretch, style.textRendering, style.letterSpacing, style.lineHeight, style.color, style.direction,
          style.textTransform, style.textDecorationLine, style.textDecorationThickness, scale, fontVersion].join('|');
        const padding = 2;
        const textRect = { left: rect.left - padding, top: rect.top - padding, width: rect.width + padding * 2, height: rect.height + padding * 2 };
        const resource = raster(element, 'text', signature, textRect, () => {
          for (const node of textNodes) drawText(node, style, element);
        });
        add(textRect, opacity, { resource });
      }
      if (!skipChildren) paintChildren(element, opacity);
    }
    activeClip = previousClip;
  };

  return {
    texture: layer.texture,
    resize(w, h, ratio) {
      width = w;
      height = h;
      dpr = ratio;
      scene.resizeLayer(layer, Math.round(w * ratio), Math.round(h * ratio));
      dirty = true;
    },
    update(cache = {}, nextFontVersion = 0) {
      fontVersion = nextFontVersion;
      for (const [video, frame] of videoFrames) {
        if (!video.isConnected) releaseVideo(video, frame);
      }
      for (const [element, slots] of resources) {
        if (!element.isConnected) {
          for (const resource of slots.values()) scene.release(resource);
          resources.delete(element);
        }
      }
      styles = cache.styles ||= new WeakMap();
      rectangles = cache.rectangles ||= new WeakMap();
      textRectangles = cache.textRectangles ||= new WeakMap();
      commands = [];
      activeClip = { left: 0, top: 0, right: width, bottom: height };
      background = null;
      if (!transparentBackground) {
        background = getStyle(document.body).backgroundColor;
        if (transparent(background)) background = '#000';
      }
      paint(root);
      visibleVideos = new Set(commands.filter(command => command.video).map(command => command.video));
      canvasCommands = commands.filter(command => command.canvasSource);
      // Bound inactive raster memory on long pages without evicting visible textures.
      const active = new Set(commands.map(command => command.resource));
      let bytes = 0;
      for (const slots of resources.values()) for (const resource of slots.values()) bytes += (resource.width || 0) * (resource.height || 0) * 4;
      for (const [element, slots] of resources) {
        if (bytes <= 32 * 1024 * 1024) break;
        for (const [slot, resource] of slots) {
          if (active.has(resource)) continue;
          bytes -= (resource.width || 0) * (resource.height || 0) * 4;
          scene.release(resource);
          slots.delete(slot);
          if (slot === 'media' && videoFrames.has(element)) releaseVideo(element, videoFrames.get(element));
        }
        if (!slots.size) resources.delete(element);
      }
      dirty = true;
    },
    draw() {
      for (const video of visibleVideos) {
        const frame = videoFrames.get(video);
        if (!frame) continue;
        const changed = video.requestVideoFrameCallback ? frame.dirty : frame.time !== video.currentTime;
        // A loop seek keeps the last valid GPU texture until the next decoded frame.
        if (video.readyState >= 2 && !video.seeking && changed) {
          scene.upload(frame.resource, video);
          frame.time = video.currentTime;
          frame.dirty = false;
          dirty = true;
        }
      }
      for (const command of canvasCommands) {
        scene.upload(command.resource, command.canvasSource);
        dirty = true;
      }
      if (dirty) {
        scene.draw(layer, commands, width, height, background);
        dirty = false;
      }
      return commands.length > 0;
    },
    destroy() {
      for (const [video, frame] of videoFrames) releaseVideo(video, frame);
      range.detach();
      backgrounds.clear();
      for (const slots of resources.values()) for (const resource of slots.values()) scene.release(resource);
      resources.clear();
      commands = [];
      canvas.width = 0;
      canvas.height = 0;
    },
  };
};
